import { computed, makeObservable, observable, action } from "mobx";
import { DbRecording, UUID } from "./DatabaseTypes";
import { requireEvidenceDatabase } from "./getDatabase";
import { NetworkTrafficRecordingDatabaseState } from '../NetworkTrafficRecordingDatabaseState';
import { friendlyTimeSince } from "../../lib/utils/friendlyTimeSince";
import type { EvidenceDatabase } from "./EvidenceDbStruct";


export class DbRecordingEntryReader {
	_recording: DbRecording;
	populatedName: string | undefined;
	trafficState: NetworkTrafficRecordingDatabaseState;

	@computed get recording() {
		return { ...this._recording };
	}
	@computed get recordingUuid() {
		return this.recording.recordingUuid;
	}

	protected static fetchFromDb = (db: EvidenceDatabase, recordingUuid: UUID) =>
		db.get("recordings", recordingUuid);

	setRecording = action ( (recording: DbRecording) => {
		this._recording = {...recording};
	})

	updateFromDatabase = async () => {
		const updatedRecording = await DbRecordingEntryReader.fetchFromDb(this.db, this.recording.recordingUuid);
		if (updatedRecording != null) {
			this.setRecording(updatedRecording);
		}
	};

	@computed get mostCommonPageRegisteredDomainInNetworkRequests() {
		const [mostCommonPageRegisteredDomainInNetworkRequests] = [...this.trafficState.sourcePageOriginStatisticsTree.values()].sort( (a, b) => b.numberOfRequests - a.numberOfRequests );
		if (mostCommonPageRegisteredDomainInNetworkRequests != null) {
			return mostCommonPageRegisteredDomainInNetworkRequests.registeredDomain;
		}
	}

	@computed get placeholderName() {
		if (this.recording.name != null && this.recording.name.length > 0) {
			return this.recording.name;
		} else if (this.mostCommonPageRegisteredDomainInNetworkRequests != null) {
			return `${this.mostCommonPageRegisteredDomainInNetworkRequests} at ${new Date(this.recording.startTimeIso).toLocaleString()}`;
		}
		return `Recording started ${(new Date(this.recording.startTimeIso).toLocaleString())}`;
	}


	@computed get name() {
		return this.recording.name;
	}

	@computed get nameOrDescription() {
		if (this.name != null && this.name.length > 0) return this.name;
		if (this.inProgress) return "Recording in progress";
		const dataSummary = this.recording.dataSummary;
		if (dataSummary != null) return `Recording ended ${ friendlyTimeSince(dataSummary.whenLastObservedIso) }`;
		return `Recording started ${ friendlyTimeSince(this.recording.startTimeIso) }`;
	}


	@computed get status() {
		return this.recording.status;
	}

	@computed get inProgress() {
		return Array.isArray(this.status) || this.status == 'allTabs';
	}


	getName = async () => {
		if (this.name != null && this.name.length > 0) {
			return this.name;
		} else {
			await this.trafficState.update();
			return this.placeholderName;
		}
	}

	constructor(
		readonly db: EvidenceDatabase,
		initialValue: DbRecording,
	) {
		this._recording = initialValue;
		makeObservable(this, {_recording: observable});
		this.trafficState = NetworkTrafficRecordingDatabaseState.forRecordingUuid(this.recording.recordingUuid);
	}

	static #forRecordingUuid = new Map<UUID, DbRecordingEntryReader>();
	static forRecordingUuid = (recordingUuid: UUID) => this.#forRecordingUuid.get(recordingUuid);

	static fromDbAndRecording = (db: EvidenceDatabase, recording: DbRecording) => {
		let reader = DbRecordingEntryReader.#forRecordingUuid.get(recording.recordingUuid);
		if (reader == null) {
			reader = new DbRecordingEntryReader(db, recording);
			DbRecordingEntryReader.#forRecordingUuid.set(recording.recordingUuid, reader);
		}
		return reader;
	}

	static fromRecordingUuid = async (recordingUuid: UUID) => {
		let reader = DbRecordingEntryReader.#forRecordingUuid.get(recordingUuid);
		if (reader == null) {
			const db = await requireEvidenceDatabase();
			const recording = await DbRecordingEntryReader.fetchFromDb(db, recordingUuid);
			reader = recording != null ? new DbRecordingEntryReader(db, recording) : undefined;
		}
		return reader;
	}
		
}
